import PropTypes from 'prop-types'
import React, { Fragment, useEffect, useState, useCallback, useMemo } from 'react'
import { Row, T } from '@nv/react-commons/src/Components'
import { Text } from 'components/Text'
import { Colors } from 'themes'
import { PrintFormatModal } from './PrintFormatModal'
import { PRINTED_STATUS, SOURCE_PRINT_MODAL } from './constants'
import { mpTrackPrintedAirwayBill } from 'containers/FPLLogging/mixpanel'
import { PAGE_NAMES } from 'containers/FPLLogging/constants'
import { ROUTES } from 'containers/Base/constants'
import { createStructuredSelector } from 'reselect'
import { connect } from 'react-redux'
import { compose } from 'redux'
import { selectPrinterSettings } from 'containers/Base/selectors'
import { trackPrintedAirwayBill } from 'containers/FPLLogging/rum'
import {
  AwbModal,
  BillCountText,
  ChangeBillFormatButton,
  FileInfoCol,
  PDFSymbol,
  PrintButton,
  PrintButtonContainer
} from './styles'
import { createLabels } from 'services/api/fplApi'
import { getBlobUrl } from './printPdf'
import { Link } from 'components/Link'

const REQUEST_STATUS = {
  PENDING: 'pending',
  SUCCESS: 'success',
  FAILED: 'failed'
}

const initState = {
  billsPerPage: null,
  waybillRequestsByIndexGroup: {},
}

const MMCC_REQUEST_SIZE = 50

export const MmccAwbPrinterModal = ({
  batchId,
  onCancel,
  totalOrderCount,
  trackingIds,
  visible,
  source,
  orders,
  countryVendor,
  printerSettings,
  enabledPortrait,
  modalType
}) => {
  const [state, setState] = useState(initState)
  const { billsPerPage, waybillRequestsByIndexGroup } = state

  useEffect(() => {
    if (!visible) {
      setState(prevState => ({
        ...prevState,
        waybillRequestsByIndexGroup: {},
        billsPerPage: null
      }))
    }
  }, [visible])

  useEffect(() => {
    const chunks = []
    for (let i = 0; i < trackingIds.length; i += MMCC_REQUEST_SIZE) {
      chunks.push(trackingIds.slice(i, i + MMCC_REQUEST_SIZE))
    }

    const awbRequestsByIndexGroup = chunks.reduce((acc, chunk, index) => {
      const fromIndex = index * MMCC_REQUEST_SIZE
      const toIndex = Math.min((index + 1) * MMCC_REQUEST_SIZE - 1, trackingIds.length - 1)
      const existingRequest = waybillRequestsByIndexGroup[`${fromIndex}-${toIndex}`]
      acc[`${fromIndex}-${toIndex}`] = {
        trackingIds: chunk,
        url: existingRequest?.status === REQUEST_STATUS.SUCCESS ? existingRequest?.url : '',
        status: existingRequest?.status === REQUEST_STATUS.SUCCESS ? existingRequest?.status : REQUEST_STATUS.PENDING
      }
      return acc
    }, {})

    setState(prevState => ({
      ...prevState,
      waybillRequestsByIndexGroup: awbRequestsByIndexGroup
    }))
  }, [trackingIds])

  const getMmccPDFFile = async () => {
    const requestPromises = Object.entries(waybillRequestsByIndexGroup).map(async ([indexGroup, { trackingIds }]) => {
      try {
        const res = await createLabels({
          parcel_ids: trackingIds,
          page_layout: billsPerPage === 4 ? 3 : billsPerPage
        })

        return {
          indexGroup,
          status: res?.data ? REQUEST_STATUS.SUCCESS : REQUEST_STATUS.FAILED,
          url: res?.data
        }
      } catch {
        return {
          indexGroup,
          status: REQUEST_STATUS.FAILED,
          url: ''
        }
      }
    })

    const results = await Promise.all(requestPromises)

    const awbRequestsByIndexGroup = results.reduce(
      (acc, { indexGroup, status, url }) => ({
        ...acc,
        [indexGroup]: { status, url, trackingIds: waybillRequestsByIndexGroup[indexGroup].trackingIds }
      }),
      {}
    )

    setState(prevState => ({
      ...prevState,
      waybillRequestsByIndexGroup: awbRequestsByIndexGroup
    }))
  }

  useEffect(() => {
    if (billsPerPage && billsPerPage > 0 && visible) {
      getMmccPDFFile()
    }
  }, [billsPerPage, visible, modalType])

  const handleSelectPrintOpt = otp => {
    setState(prevState => ({
      ...prevState,
      billsPerPage: otp
    }))
  }

  const handleChangeAwbFormat = () => {
    setState(prevState => ({
      ...prevState,
      billsPerPage: null
    }))
  }

  const mpTracking = useCallback(() => {
    mpTrackPrintedAirwayBill({
      status: PRINTED_STATUS.SUCCESS,
      source,
      printedOrder: totalOrderCount,
      pageName: PAGE_NAMES[ROUTES.FPL_OC_REQUEST_BY_BATCH]
    })
    trackPrintedAirwayBill({
      status: PRINTED_STATUS.SUCCESS,
      source,
      printedOrder: totalOrderCount,
      pageName: PAGE_NAMES[ROUTES.FPL_OC_REQUEST_BY_BATCH]
    })
  }, [source, totalOrderCount])

  const handleClickPrint = useCallback((msSaveOrOpenBlob, blob) => {
    msSaveOrOpenBlob(blob, 'awb.pdf')
  }, [])

  const handleRetryPrint = useCallback(
    async indexGroup => {
      setState(prevState => ({
        ...prevState,
        waybillRequestsByIndexGroup: {
          ...prevState.waybillRequestsByIndexGroup,
          [indexGroup]: {
            ...prevState.waybillRequestsByIndexGroup[indexGroup],
            status: REQUEST_STATUS.PENDING
          }
        }
      }))

      const trackingIds = waybillRequestsByIndexGroup[indexGroup].trackingIds
      try {
        const res = await createLabels({
          parcel_ids: trackingIds,
          page_layout: billsPerPage === 4 ? 3 : billsPerPage
        })

        setState(prevState => ({
          ...prevState,
          waybillRequestsByIndexGroup: {
            ...prevState.waybillRequestsByIndexGroup,
            [indexGroup]: {
              ...prevState.waybillRequestsByIndexGroup[indexGroup],
              status: REQUEST_STATUS.SUCCESS,
              url: res?.data
            }
          }
        }))
      } catch (error) {
        setState(prevState => ({
          ...prevState,
          waybillRequestsByIndexGroup: {
            ...prevState.waybillRequestsByIndexGroup,
            [indexGroup]: {
              ...prevState.waybillRequestsByIndexGroup[indexGroup],
              status: REQUEST_STATUS.FAILED,
              url: ''
            }
          }
        }))
      }
    },
    [countryVendor, billsPerPage, printerSettings, enabledPortrait, waybillRequestsByIndexGroup]
  )

  const getPrintProps = (url, status, indexGroup) => {
    if (!status || status === REQUEST_STATUS.PENDING) {
      return {}
    }

    if (status === REQUEST_STATUS.FAILED) {
      return {
        onClick: () => handleRetryPrint(indexGroup)
      }
    }

    const msSaveOrOpenBlob = window.navigator && window.navigator.msSaveOrOpenBlob
    return {
      onClick: msSaveOrOpenBlob ? handleClickPrint(msSaveOrOpenBlob, url) : mpTracking,
      href: msSaveOrOpenBlob ? undefined : getBlobUrl(url),
      target: '_blank'
    }
  }

  const renderPrintButtons = (url, status, indexGroup) => {
    const buttonConfig = {
      [REQUEST_STATUS.PENDING]: {
        type: 'default',
        text: 'loading'
      },
      [REQUEST_STATUS.FAILED]: { type: 'secondary', text: 'order_retry' },
      [REQUEST_STATUS.SUCCESS]: { type: 'primary', text: 'print' }
    }

    const { type, text } = buttonConfig[status]
    const printProps = getPrintProps(url, status, indexGroup)

    return (
      <PrintButton
        type={type}
        {...printProps}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <T allCaps id={text} />
      </PrintButton>
    )
  }

  const renderDownloadAllWaybills = () => {
    const successWaybills = Object.values(waybillRequestsByIndexGroup).filter(request => {
      return request?.url && request.status === REQUEST_STATUS.SUCCESS
    })

    if (!successWaybills.length) {
      return null
    }

    const handleDownloadAll = () => {
      successWaybills.forEach(waybill => {
        const openUrl = getBlobUrl(waybill.url)
        window.open(openUrl, '_blank')
      })
    }

    return (
      <Link onClick={handleDownloadAll}>
        <T id='print_all_success_labels' />
      </Link>
    )
  }

  const renderAwbPrintRows = useCallback(() => {
    if (Object.keys(waybillRequestsByIndexGroup).length === 0) {
      return null
    }
    return (
      <PrintButtonContainer>
        {Object.keys(waybillRequestsByIndexGroup).map((indexGroup, index) => {
          const requestGroup = waybillRequestsByIndexGroup[indexGroup]
          const { trackingIds, url, status } = requestGroup
          const startIndex = index * MMCC_REQUEST_SIZE + 1
          const endIndex = Math.max(startIndex + trackingIds.length - 1, trackingIds.length)

          return (
            <Fragment key={index}>
              <Row align='middle' justify='space-between' type='flex' style={{ marginBottom: 10 }}>
                <PDFSymbol>
                  <Text color={Colors.white} size={14} type='bold'>
                    .PDF
                  </Text>
                </PDFSymbol>
                <FileInfoCol>
                  <Text
                    color={Colors.greyishBrown}
                    id='awb_pdf_file_name'
                    size={13}
                    type='bold'
                    values={{
                      batchId: batchId,
                      first: startIndex,
                      last: endIndex
                    }}
                  />
                  <Text
                    color={Colors.pinkishGrey}
                    id='x_orders_y_pages'
                    size={12}
                    values={{ x: trackingIds.length, y: Math.ceil(trackingIds.length / billsPerPage) }}
                  />
                </FileInfoCol>

                {renderPrintButtons(url, status, indexGroup)}
              </Row>
            </Fragment>
          )
        })}
      </PrintButtonContainer>
    )
  }, [waybillRequestsByIndexGroup])

  return (
    <>
      <PrintFormatModal
        visible={visible && !billsPerPage}
        onCancel={onCancel}
        onSelectAwbFormat={handleSelectPrintOpt}
        orders={orders}
      />
      <AwbModal
        maskClosable={false}
        footer={
          <>
            <BillCountText color={Colors.warmGrey}>
              <T id='x_bills_per_page' values={{ x: billsPerPage }} />
            </BillCountText>
            <>{renderDownloadAllWaybills()}</>
            <ChangeBillFormatButton
              color={Colors.pinkishGrey}
              data-analyticsid='4plChangeAWBFormat'
              key='change_bill_format'
              onClick={handleChangeAwbFormat}
            >
              <T id='change_bill_format' />
            </ChangeBillFormatButton>
          </>
        }
        onCancel={onCancel}
        title={<T id='print_airway_bills' />}
        visible={visible && !!billsPerPage}
      >
        {renderAwbPrintRows()}
      </AwbModal>
    </>
  )
}

MmccAwbPrinterModal.propTypes = {
  batchId: PropTypes.number,
  onCancel: PropTypes.func.isRequired,
  totalOrderCount: PropTypes.number,
  trackingIds: PropTypes.arrayOf(PropTypes.string),
  visible: PropTypes.bool,
  source: PropTypes.string,
  orders: PropTypes.array,
  countryVendor: PropTypes.string
}

MmccAwbPrinterModal.defaultProps = {
  visible: false,
  source: SOURCE_PRINT_MODAL.LIST,
  orders: [],
  countryVendor: ''
}

const mapStatetoProps = createStructuredSelector({
  printerSettings: selectPrinterSettings()
})

const withConnect = connect(mapStatetoProps)

export const AwbPrinterModalV1 = compose(withConnect)(MmccAwbPrinterModal)
